import { useRef, useState, type ReactNode } from "react";
import { ChevronDown, Columns3, Lock } from "lucide-react";
import { useClickOutside } from "../../hooks/use-click-outside";
import { cn } from "../../lib/utils";

export interface MultiSelectOption { value: string; label: string; locked?: boolean }

interface MultiSelectProps {
  buttonLabel: string;
  options: MultiSelectOption[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  defaultSelected: string[];
  icon?: ReactNode;
}

export function MultiSelect({ buttonLabel, options, selected, onChange, defaultSelected, icon }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  const toggle = (opt: MultiSelectOption) => {
    if (opt.locked) return;
    const next = new Set(selected);
    next.has(opt.value) ? next.delete(opt.value) : next.add(opt.value);
    onChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-lg border border-line bg-soft px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-line/60"
      >
        {icon ?? <Columns3 className="h-4 w-4 text-ink-3" />}
        {buttonLabel}
        <span className="rounded-full bg-elevated px-1.5 py-0.5 font-mono text-[10px] font-bold text-ink-3">
          {selected.size}/{options.length}
        </span>
        <ChevronDown className={cn("h-4 w-4 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-1 w-60 rounded-xl border border-line bg-elevated py-2 shadow-lg">
          <p className="px-3 pb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
            Visible columns
          </p>
          <ul className="max-h-72 overflow-y-auto">
            {options.map((opt) => {
              const on = opt.locked || selected.has(opt.value);
              return (
                <li key={opt.value}>
                  <button
                    role="option"
                    aria-selected={on}
                    disabled={opt.locked}
                    onClick={() => toggle(opt)}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                      opt.locked ? "cursor-not-allowed text-ink-3" : "text-ink-2 hover:bg-soft",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border",
                        on ? "border-brand bg-brand text-white" : "border-line bg-elevated",
                      )}
                    >
                      {on && <svg viewBox="0 0 10 8" className="h-2 w-2 fill-none stroke-current stroke-2"><path d="M1 4l2.5 2.5L9 1" /></svg>}
                    </span>
                    <span className="flex-1">{opt.label}</span>
                    {opt.locked && <Lock className="h-3 w-3" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="mt-1 border-t border-line px-3 pt-2">
            <button
              onClick={() => onChange(new Set(defaultSelected))}
              className="text-xs font-semibold text-brand hover:underline"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
